import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import {
  SAMPLE_DEVIATION_CARD,
  formatError,
  formatHeading,
  normalizeDeg,
  signedError,
  type CompassError,
} from "@nav-games/compass";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface DeviationLookupProps {
  onNavigate: (to: string) => void;
}

const MODULE = "compass";
const GAME = "deviation";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  compass: number;
  /// Heading of the card row that applies, and the deviation it gives.
  rowHeading: number;
  deviation: CompassError;
  /// Correct magnetic heading.
  target: number;
  choices: readonly string[];
}

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}

/// Circular distance between two headings, 0..180.
function angularDist(a: number, b: number): number {
  const d = normalizeDeg(a - b);
  return d > 180 ? 360 - d : d;
}

function nearestRow(compass: number): (typeof SAMPLE_DEVIATION_CARD)[number] {
  let best = SAMPLE_DEVIATION_CARD[0]!;
  let bestGap = angularDist(best.headingDeg, compass);
  for (const row of SAMPLE_DEVIATION_CARD) {
    const gap = angularDist(row.headingDeg, compass);
    if (gap < bestGap) {
      best = row;
      bestGap = gap;
    }
  }
  return best;
}

function buildCard(): Card {
  // Ask at a heading clearly nearest a single card row — within ±10° of
  // it, well inside the 30° row spacing. A heading exactly between two
  // rows (e.g. 075° between 060° and 090°) has a contestable answer, so
  // we never generate one. Only rows with a non-zero deviation are used
  // so applying it actually changes the heading.
  const nonZeroRows = SAMPLE_DEVIATION_CARD.filter((r) => r.deviation.degrees > 0);
  const base = nonZeroRows[Math.floor(Math.random() * nonZeroRows.length)]!;
  const compass = normalizeDeg(base.headingDeg + randInt(-10, 10));
  const row = nearestRow(compass);
  const signed = signedError(row.deviation);
  const target = normalizeDeg(compass + signed);

  const pool = [
    normalizeDeg(compass), // forgot to apply the deviation
    normalizeDeg(compass - signed), // applied it the wrong way
    normalizeDeg(compass + 2 * signed), // applied it twice
  ];
  const chosen: number[] = [];
  const seen = new Set<number>([target]);
  for (const cand of pool) {
    if (chosen.length >= CHOICES_PER_CARD - 1) break;
    if (!seen.has(cand)) {
      seen.add(cand);
      chosen.push(cand);
    }
  }
  let off = 3;
  while (chosen.length < CHOICES_PER_CARD - 1) {
    for (const cand of [normalizeDeg(target + off), normalizeDeg(target - off)]) {
      if (chosen.length >= CHOICES_PER_CARD - 1) break;
      if (!seen.has(cand)) {
        seen.add(cand);
        chosen.push(cand);
      }
    }
    off += 3;
  }
  const all = [target, ...chosen].map((n) => `${formatHeading(n)}°`);
  for (let i = all.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = all[i]!;
    all[i] = all[j]!;
    all[j] = ai;
  }
  return {
    compass,
    rowHeading: row.headingDeg,
    deviation: row.deviation,
    target,
    choices: all,
  };
}

export function DeviationLookup({ onNavigate }: DeviationLookupProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState<number>(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef<number>(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (choice: string) => {
      if (chosen || result) return;
      const ok = choice === `${formatHeading(card.target)}°`;
      const attempt: Omit<Attempt, "module"> = {
        char: "deviation",
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(choice);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/chartwork")}
      />
    );
  }

  const revealed = chosen !== null;
  const correctStr = `${formatHeading(card.target)}°`;
  const op = card.deviation.hand === "E" ? "+" : "−";

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "compass" : null} onClose={() => setHelpOpen(false)} />
      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION}
      </p>

      {/* The deviation card — read the row nearest your heading. */}
      <div className="card">
        <h3 style={{ marginTop: 0, fontSize: 15 }}>Deviation card</h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
            gap: 6,
          }}
        >
          {SAMPLE_DEVIATION_CARD.map((row) => {
            const isUsed = revealed && row.headingDeg === card.rowHeading;
            return (
              <div
                key={row.headingDeg}
                style={{
                  textAlign: "center",
                  padding: "6px 4px",
                  borderRadius: 6,
                  fontSize: 13,
                  border: `1px solid ${isUsed ? "var(--accent)" : "var(--border)"}`,
                  background: isUsed ? "var(--accent-soft)" : "transparent",
                }}
              >
                <div style={{ fontWeight: 700 }}>{formatHeading(row.headingDeg)}°</div>
                <div className="muted">{formatError(row.deviation)}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <p style={{ marginTop: 0, marginBottom: 12 }}>
          You are steering <strong>{formatHeading(card.compass)}°</strong> by the
          compass. Reading the deviation from the card, what is your{" "}
          <strong>Magnetic</strong> heading?
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c === correctStr;
            const isChosen = c === chosen;
            return (
              <button
                key={c}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c)}
                style={{
                  fontSize: 20,
                  padding: "14px 0",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: 12,
              borderRadius: 6,
              background:
                chosen === correctStr
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === correctStr
                ? `✓ Correct — ${correctStr} Magnetic.`
                : `✗ Not quite — ${correctStr} Magnetic.`}
            </p>
            <p className="small muted" style={{ margin: "8px 0 0" }}>
              Nearest card row is {formatHeading(card.rowHeading)}° →{" "}
              deviation {formatError(card.deviation)}. Compass{" "}
              {formatHeading(card.compass)}° {op} {card.deviation.degrees}° ={" "}
              {correctStr} Magnetic (deviation East adds, West subtracts).
            </p>
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next question →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/chartwork")}>
        ← Back to Chartwork
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div
        className="row"
        style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}
      >
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Chartwork
        </button>
      </div>
    </>
  );
}
